import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import "../../styles/lab/salles.css";
import {
  STATUTS_SALLE,
  TYPES_SALLE,
  createSalle,
  deleteSalle,
  getPlanningSalle,
  getSalles,
  getSallesDisponibles,
  updateSalle,
} from "../../services/lab/salleService";

const SALLE_VIDE = {
  nom: "", numero: "", batiment: "", etage: "", capacite: "",
  localisation: "", type_salle: "classe", statut: "disponible",
  responsable: "", telephone_contact: "", notes: "",
};

const libelle = (liste, valeur) =>
  liste.find((x) => x.value === valeur)?.label || valeur || "—";

export default function Salles() {
  const [salles, setSalles]     = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur]     = useState(null);
  const [flash, setFlash]       = useState(null);

  const [recherche, setRecherche] = useState("");
  const [filtreType, setFiltreType] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("");

  const [modale, setModale]     = useState(null); // 'creer' | 'modifier' | 'planning'
  const [courante, setCourante] = useState(null);
  const [form, setForm]         = useState(SALLE_VIDE);
  const [erreursForm, setErreursForm] = useState({});
  const [planning, setPlanning] = useState([]);
  const [enregistrement, setEnregistrement] = useState(false);

  // Recherche de disponibilité
  const [creneau, setCreneau] = useState({ debut: "", fin: "", capacite: "" });
  const [dispos, setDispos]   = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      setSalles(await getSalles());
      setErreur(null);
    } catch (e) {
      setErreur("Impossible de charger les salles.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const notifier = (msg, type = "succes") => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 4000);
  };

  const filtrees = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return salles.filter((s) => {
      if (filtreType && s.type_salle !== filtreType) return false;
      if (filtreStatut && s.statut !== filtreStatut) return false;
      if (!q) return true;
      return [s.nom, s.batiment, s.numero, s.localisation]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [salles, recherche, filtreType, filtreStatut]);

  const ouvrirCreation = () => {
    setForm(SALLE_VIDE); setErreursForm({}); setCourante(null); setModale("creer");
  };

  const ouvrirModification = (salle) => {
    setForm({ ...SALLE_VIDE, ...salle, etage: salle.etage ?? "", capacite: salle.capacite ?? "" });
    setErreursForm({}); setCourante(salle); setModale("modifier");
  };

  const ouvrirPlanning = async (salle) => {
    setCourante(salle); setModale("planning"); setPlanning([]);
    try { setPlanning(await getPlanningSalle(salle.id)); }
    catch { notifier("Impossible de charger le planning.", "erreur"); }
  };

  const enregistrer = async (e) => {
    e.preventDefault();
    setEnregistrement(true); setErreursForm({});
    const payload = {
      ...form,
      etage: form.etage === "" ? null : Number(form.etage),
      capacite: Number(form.capacite),
    };
    try {
      if (courante) {
        await updateSalle(courante.id, payload);
        notifier(`Salle « ${form.nom} » modifiée.`);
      } else {
        await createSalle(payload);
        notifier(`Salle « ${form.nom} » créée.`);
      }
      setModale(null);
      charger();
    } catch (err) {
      setErreursForm(err.response?.data || { detail: "Erreur inattendue." });
    } finally {
      setEnregistrement(false);
    }
  };

  const supprimer = async (salle) => {
    if (!window.confirm(`Supprimer la salle « ${salle.nom} » ?`)) return;
    try {
      await deleteSalle(salle.id);
      notifier(`Salle « ${salle.nom} » supprimée.`);
      charger();
    } catch (err) {
      // 409 : des sessions occupent la salle. Le backend explique lesquelles.
      notifier(err.response?.data?.message || "Suppression impossible.", "erreur");
    }
  };

  const chercherDispos = async (e) => {
    e.preventDefault();
    if (!creneau.debut || !creneau.fin) return;
    try {
      const iso = (v) => new Date(v).toISOString();
      setDispos(await getSallesDisponibles(iso(creneau.debut), iso(creneau.fin), creneau.capacite));
    } catch (err) {
      notifier(err.response?.data?.error || "Recherche impossible.", "erreur");
    }
  };

  const affichees = dispos ?? filtrees;

  return (
    <Layout>
      <div className="salles-page">
        <header className="salles-header">
          <div>
            <h1><i className="fa-solid fa-door-open"></i> Salles</h1>
            <p className="salles-sous-titre">
              {salles.length} salle{salles.length > 1 ? "s" : ""} ·{" "}
              {salles.reduce((n, s) => n + (s.nb_equipements || 0), 0)} équipements
            </p>
          </div>
          <button className="btn-primaire" onClick={ouvrirCreation}>
            <i className="fa-solid fa-plus"></i> Nouvelle salle
          </button>
        </header>

        {flash && <div className={`salles-flash ${flash.type}`}>{flash.msg}</div>}

        {/* Recherche de disponibilité — impossible dans le Lab, qui stockait
            un statut « Occupée » au lieu de croiser les réservations. */}
        <form className="salles-dispo" onSubmit={chercherDispos}>
          <div className="salles-dispo-titre">
            <i className="fa-solid fa-calendar-check"></i> Trouver une salle libre
          </div>
          <div className="salles-dispo-champs">
            <label>
              Du
              <input type="datetime-local" value={creneau.debut}
                     onChange={(e) => setCreneau({ ...creneau, debut: e.target.value })} required />
            </label>
            <label>
              Au
              <input type="datetime-local" value={creneau.fin}
                     onChange={(e) => setCreneau({ ...creneau, fin: e.target.value })} required />
            </label>
            <label>
              Capacité min.
              <input type="number" min="1" placeholder="—" value={creneau.capacite}
                     onChange={(e) => setCreneau({ ...creneau, capacite: e.target.value })} />
            </label>
            <button type="submit" className="btn-secondaire">
              <i className="fa-solid fa-magnifying-glass"></i> Chercher
            </button>
            {dispos && (
              <button type="button" className="btn-lien" onClick={() => setDispos(null)}>
                Voir toutes les salles
              </button>
            )}
          </div>
          {dispos && (
            <div className="salles-dispo-resultat">
              {dispos.length
                ? `${dispos.length} salle(s) libre(s) sur ce créneau`
                : "Aucune salle libre sur ce créneau"}
            </div>
          )}
        </form>

        {!dispos && (
          <div className="salles-filtres">
            <div className="salles-recherche">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input placeholder="Rechercher une salle, un bâtiment…" value={recherche}
                     onChange={(e) => setRecherche(e.target.value)} />
            </div>
            <select value={filtreType} onChange={(e) => setFiltreType(e.target.value)}>
              <option value="">Tous les types</option>
              {TYPES_SALLE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
              <option value="">Tous les statuts</option>
              {STATUTS_SALLE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        )}

        {chargement && <div className="salles-etat"><i className="fa-solid fa-spinner fa-spin"></i> Chargement…</div>}
        {erreur && <div className="salles-etat erreur">{erreur}</div>}

        {!chargement && !erreur && (
          <div className="salles-grille">
            {affichees.map((salle) => (
              <article key={salle.id} className={`salle-carte statut-${salle.statut}`}>
                <div className="salle-carte-tete">
                  <div>
                    <h3>{salle.nom}</h3>
                    <span className="salle-type">{libelle(TYPES_SALLE, salle.type_salle)}</span>
                  </div>
                  <span className={`salle-badge ${salle.statut}`}>
                    {libelle(STATUTS_SALLE, salle.statut)}
                  </span>
                </div>

                <div className="salle-infos">
                  <div><i className="fa-solid fa-users"></i> {salle.capacite} places</div>
                  <div><i className="fa-solid fa-location-dot"></i> {salle.localisation || salle.batiment || "—"}</div>
                  <div><i className="fa-solid fa-plug"></i> {salle.nb_equipements} équipement(s)</div>
                  <div>
                    <i className="fa-solid fa-calendar-days"></i> {salle.sessions_a_venir} session(s) à venir
                  </div>
                </div>

                {salle.occupee_maintenant && (
                  <div className="salle-occupee">
                    <i className="fa-solid fa-circle"></i> Occupée en ce moment
                  </div>
                )}

                <div className="salle-actions">
                  <button onClick={() => ouvrirPlanning(salle)} title="Planning">
                    <i className="fa-solid fa-calendar-days"></i>
                  </button>
                  <button onClick={() => ouvrirModification(salle)} title="Modifier">
                    <i className="fa-solid fa-pen"></i>
                  </button>
                  <button className="danger" onClick={() => supprimer(salle)} title="Supprimer">
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </article>
            ))}
            {!affichees.length && (
              <div className="salles-vide">
                <i className="fa-solid fa-door-closed"></i>
                <p>Aucune salle ne correspond.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modale création / modification */}
      {(modale === "creer" || modale === "modifier") && (
        <div className="salles-modale-fond" onClick={() => setModale(null)}>
          <div className="salles-modale" onClick={(e) => e.stopPropagation()}>
            <header>
              <h2>{modale === "creer" ? "Nouvelle salle" : `Modifier « ${courante?.nom} »`}</h2>
              <button onClick={() => setModale(null)}><i className="fa-solid fa-xmark"></i></button>
            </header>

            <form onSubmit={enregistrer}>
              <div className="champs">
                <label>Nom *
                  <input value={form.nom} required
                         onChange={(e) => setForm({ ...form, nom: e.target.value })} />
                  {erreursForm.nom && <small className="err">{erreursForm.nom}</small>}
                </label>
                <label>Numéro
                  <input value={form.numero}
                         onChange={(e) => setForm({ ...form, numero: e.target.value })} />
                </label>
                <label>Type *
                  <select value={form.type_salle}
                          onChange={(e) => setForm({ ...form, type_salle: e.target.value })}>
                    {TYPES_SALLE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
                <label>Capacité *
                  <input type="number" min="1" value={form.capacite} required
                         onChange={(e) => setForm({ ...form, capacite: e.target.value })} />
                  {erreursForm.capacite && <small className="err">{erreursForm.capacite}</small>}
                </label>
                <label>Bâtiment
                  <input value={form.batiment}
                         onChange={(e) => setForm({ ...form, batiment: e.target.value })} />
                </label>
                <label>Étage
                  <input type="number" value={form.etage}
                         onChange={(e) => setForm({ ...form, etage: e.target.value })} />
                </label>
                <label className="large">Localisation
                  <input value={form.localisation}
                         onChange={(e) => setForm({ ...form, localisation: e.target.value })} />
                </label>
                <label>Statut
                  <select value={form.statut}
                          onChange={(e) => setForm({ ...form, statut: e.target.value })}>
                    {STATUTS_SALLE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <small className="aide">
                    L'occupation n'est pas un statut : elle se déduit des sessions.
                  </small>
                </label>
                <label>Responsable
                  <input value={form.responsable}
                         onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
                </label>
                <label className="large">Notes
                  <textarea rows="2" value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </label>
              </div>

              {erreursForm.detail && <div className="salles-flash erreur">{erreursForm.detail}</div>}

              <footer>
                <button type="button" className="btn-lien" onClick={() => setModale(null)}>Annuler</button>
                <button type="submit" className="btn-primaire" disabled={enregistrement}>
                  {enregistrement ? "Enregistrement…" : "Enregistrer"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Modale planning */}
      {modale === "planning" && (
        <div className="salles-modale-fond" onClick={() => setModale(null)}>
          <div className="salles-modale" onClick={(e) => e.stopPropagation()}>
            <header>
              <h2>Planning — {courante?.nom}</h2>
              <button onClick={() => setModale(null)}><i className="fa-solid fa-xmark"></i></button>
            </header>
            <div className="salle-planning">
              {planning.length ? planning.map((s) => (
                <div key={s.id} className={`planning-ligne statut-${s.statut}`}>
                  <div className="planning-dates">
                    {s.date_debut ? new Date(s.date_debut).toLocaleDateString("fr-FR") : "—"}
                    {" → "}
                    {s.date_fin ? new Date(s.date_fin).toLocaleDateString("fr-FR") : "—"}
                  </div>
                  <div className="planning-titre">
                    <strong>{s.intitule_session}</strong>
                    <span>{s.formation_nom}</span>
                  </div>
                  <div className="planning-meta">
                    <span className={`salle-badge ${s.statut}`}>{s.statut_display}</span>
                    <span>{s.inscrits} inscrit(s)</span>
                    {!!s.formateurs.length && <span>{s.formateurs.join(", ")}</span>}
                  </div>
                </div>
              )) : (
                <div className="salles-vide">
                  <i className="fa-regular fa-calendar"></i>
                  <p>Aucune session ne réserve cette salle.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
